"use client";
import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { getSecciones } from "@/services/secciones.service";
import { getLandingConfig } from "@/services/landingConfig.service";
import { Icon } from "@iconify/react";
import { signOut, useSession } from "next-auth/react";

const FALLBACK_MINIATURA = "/Miniatura-AviorTV-Esquina.png";

function MiniaturaImage({ src, alt }) {
  const initial = src && typeof src === "string" && src.trim() ? src : FALLBACK_MINIATURA;
  const [imgSrc, setImgSrc] = useState(initial);
  useEffect(() => {
    setImgSrc(src && typeof src === "string" && src.trim() ? src : FALLBACK_MINIATURA);
  }, [src]);
  return (
    <Image
      src={imgSrc}
      alt={alt}
      fill
      sizes="(max-width: 768px) 92vw, (max-width: 1024px) 33vw, 400px"
      className="object-cover transition duration-300 group-hover:scale-[1.02] group-hover:blur-[2px]"
      unoptimized
      onError={() => {
        if (imgSrc !== FALLBACK_MINIATURA) setImgSrc(FALLBACK_MINIATURA);
      }}
    />
  );
}

function LandingPage() {
  const [ secciones, setSecciones ] = useState(null);
  const [ error, setError ] = useState(false);
  const {data: session} = useSession()
  const [config, setConfig] = useState(null)
  const gridRef = useRef(null)
  const [showScrollHint, setShowScrollHint] = useState(false)

  // fetch estable para SSE y carga inicial — si falla (offline) mantiene cache anterior y el SW sigue sirviendo
  const fetchSecciones = async () => {
    try {
      const data = await getSecciones();
      if (Array.isArray(data)) setSecciones(data)
    } catch {
      setError(true)
    }
  }

  const fetchConfig = async () => {
    try {
      const data = await getLandingConfig();
      if (data) setConfig(data)
    } catch {}
  }

  useEffect(() => {
    fetchSecciones()
    fetchConfig()
  },[])

  // SSE dentro del mismo Next — el video en cache sigue en loop aunque el SSE se caiga
  useEffect(() => {
    let es
    let retryTimer
    let retryMs = 1000

    const connect = () => {
      try {
        es = new EventSource("/api/secciones/stream")
      } catch {
        retryTimer = setTimeout(() => {
          retryMs = Math.min(retryMs * 1.5, 30000)
          connect()
        }, retryMs)
        return
      }

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          if (data?.type === "secciones:updated") {
            fetchSecciones()
            retryMs = 1000
          }
          if (data?.type === "config:updated") {
            if (data?.config) setConfig(data.config)
            else fetchConfig()
          }
        } catch {}
      }

      es.onerror = () => {
        try { es.close() } catch {}
        retryTimer = setTimeout(() => {
          retryMs = Math.min(retryMs * 1.5, 30000)
          connect()
        }, retryMs)
      }
    }

    connect()
    return () => {
      clearTimeout(retryTimer)
      try { es?.close() } catch {}
    }
  },[])

  // flecha bounce: si la grilla no entra completa (scrollable) y no está al final, mostrar hint
  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const check = () => {
      const isHorizontalScrollable = el.scrollWidth > el.clientWidth + 10
      const horizontalAtEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 20
      const showHorizontal = isHorizontalScrollable && !horizontalAtEnd

      // desktop: si la página es más alta que el viewport y no estás al final, también mostrar
      const isPageScrollable = document.documentElement.scrollHeight > window.innerHeight + 40
      const isPageAtBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40
      const showVertical = window.innerWidth >= 768 && isPageScrollable && !isPageAtBottom

      setShowScrollHint(showHorizontal || showVertical)
    }
    // esperar a que carguen imágenes para medir bien
    const t = setTimeout(check, 300)
    check()
    el.addEventListener("scroll", check, { passive: true })
    window.addEventListener("resize", check)
    window.addEventListener("scroll", check, { passive: true })
    const ro = new ResizeObserver(check)
    ro.observe(el)
    // observar carga de imágenes dentro de la grilla
    const imgs = el.querySelectorAll("img")
    imgs.forEach((img) => img.addEventListener("load", check))
    return () => {
      clearTimeout(t)
      el.removeEventListener("scroll", check)
      window.removeEventListener("resize", check)
      window.removeEventListener("scroll", check)
      ro.disconnect()
      imgs.forEach((img) => img.removeEventListener("load", check))
    }
  }, [secciones])


  if (!secciones) {
    return <p className="text-center text-white">Cargando...</p>;
  }

  const bgUrl = config?.background || "/fondo.png"
  const logoUrl = config?.logo || "/logo.webp"
  const logoSecUrl = config?.logoSecundario // puede ser "" para eliminar
  const bienvenida = config ? (config.textoBienvenida ?? "") : "Seleccione el video a reproducir"
  const footerMsg = config?.mensajeFooter || "Avior Airlines. Todos los derechos reservados."
  const gridAncho = config?.gridAncho || "5xl"
  const gridColumnas = config?.gridColumnas || 3

  const maxWidthClass = {
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    "full": "max-w-full",
  }[gridAncho] || "max-w-5xl"

  const gridColsClass = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
  }[gridColumnas] || "md:grid-cols-3"

  const showBienvenida = typeof bienvenida === "string" && bienvenida.trim().length > 0
  const showLogoSec = typeof logoSecUrl === "string" && logoSecUrl.trim().length > 0

  return (
    <main
      className="min-h-screen w-full flex flex-col justify-between landing-body bg-cover bg-center bg-no-repeat"
      style={{ 
        backgroundImage: `url("${bgUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <main className={`${maxWidthClass} mx-auto py-4 px-4 sm:px-6 lg:px-8 flex-grow flex flex-col items-center w-full`}>
        <div className="mb-8">
          <Image
            src={logoUrl}
            alt="Logo principal"
            width={160}
            height={50}
            className="mx-auto w-full"
            unoptimized
          />
        </div>
        {showLogoSec && (
          <div className="mb-8">
            <Image
              src={logoSecUrl}
              alt="Logo secundario"
              width={280}
              height={160}
              className="mx-auto"
              unoptimized
            />
          </div>
        )}

        {showBienvenida && (
          <h2 className="text-center text-white font-extrabold text-2xl mb-11">
            <strong>{bienvenida}</strong>
          </h2>
        )}

        {/* Mobile: carrusel horizontal con scroll + snap. Desktop: grilla configurable */}
        <div className="relative w-full">
          <div
            ref={gridRef}
            className={`w-full flex md:grid ${gridColsClass} gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-4 -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-track]:bg-transparent`}
          >
            {secciones.map((seccion) => {
              if (seccion.estado) {
                return (
                  <Link
                    key={seccion._id}
                    href={`/video${seccion.rutaSeccion}`}
                    className="group flex-none w-[92%] sm:w-[360px] md:w-full snap-center rounded-md shadow-md overflow-hidden"
                  >
                    <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black/20 shadow-md">
                      <MiniaturaImage src={seccion.miniatura} alt={seccion.nombreSeccion} />
                      <div className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition-colors" />
                      <p className="text-center text-white font-bold absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] px-2 w-[90%] line-clamp-2">
                        {seccion.nombreSeccion}
                      </p>
                    </div>
                  </Link>
                );
              }
            })}
          </div>

          {/* Flecha bounce fija: visible cuando hay más contenido con scroll */}
          {showScrollHint && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
              <div className="flex flex-col items-center text-white animate-bounce bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-white/20">
                <Icon icon="solar:alt-arrow-down-bold" width="28" height="28" className="drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] md:w-7 md:h-7" />
                <span className="text-xs font-semibold drop-shadow-[0_1.2px_1.2px_rgba(0,0,0,0.8)] hidden sm:block">Desliza para ver más</span>
              </div>
            </div>
          )}
        </div>
      </main>
      <footer>
        <div className="mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          {
            !session ? 
              <Link href={"/login"} target="_blank"><Icon icon="solar:login-2-bold-duotone" width="24" height="24" className="absolute bottom-4 left-4" /></Link>
            : 
              <Link href={"#"} onClick={() => signOut()}><Icon icon="solar:logout-2-bold-duotone" width="24" height="24" className="absolute bottom-4 left-4" /></Link>
          }
          <p className="text-base font-bold">
            &copy; {new Date().getFullYear()} {footerMsg}
          </p>
        </div>
      </footer>
    </main>
  );
}

export default LandingPage;
